import {
  CheckOutlined,
  DownOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { history } from '@umijs/max';
import { App, Dropdown, Input, Spin, theme } from 'antd';
import React from 'react';
import { useParkContext } from '@/park/context';
import { APPLICATIONS_NEW_PATH } from '@/park/scope';
import type { Park } from '@/services/bff/types/park';
import './index.less';

const SEARCH_THRESHOLD = 6;

export const matchParkKeyword = (park: Park, keyword: string) => {
  const query = keyword.trim().toLowerCase();
  if (!query) {
    return true;
  }
  if (park.name.toLowerCase().includes(query)) {
    return true;
  }
  return park.code.toLowerCase().includes(query);
};

const ParkItem: React.FC<{
  park: Park;
  current?: boolean;
  switching?: boolean;
  disabled?: boolean;
  onSelect: (parkUuid: string) => void;
}> = ({ park, current, switching, disabled, onSelect }) => (
  <button
    type="button"
    className={`app-park-switcher-item${current ? ' app-park-switcher-item-current' : ''}`}
    disabled={disabled}
    onClick={() => onSelect(park.uuid)}
  >
    <span className="app-park-switcher-item-icon" aria-hidden="true">
      {switching ? <Spin size="small" /> : <EnvironmentOutlined />}
    </span>
    <span className="app-park-switcher-item-name" title={park.name}>
      {park.name}
    </span>
    {current ? (
      <span className="app-park-switcher-item-check" aria-hidden="true">
        <CheckOutlined />
      </span>
    ) : null}
  </button>
);

export const ParkSwitcher: React.FC = () => {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const { parks, parkUuid, currentPark, setParkUuid } = useParkContext();
  const [open, setOpen] = React.useState(false);
  const [keyword, setKeyword] = React.useState('');
  const [switchingUuid, setSwitchingUuid] = React.useState<string>();
  const switchingRef = React.useRef(false);

  const displayPark =
    currentPark ?? parks.find((park) => park.uuid === parkUuid);
  const switching = Boolean(switchingUuid);

  const switchableParks = React.useMemo(
    () => parks.filter((park) => park.uuid !== displayPark?.uuid),
    [displayPark?.uuid, parks],
  );

  const filteredParks = React.useMemo(
    () => switchableParks.filter((park) => matchParkKeyword(park, keyword)),
    [keyword, switchableParks],
  );

  const closePanel = React.useCallback(() => {
    setOpen(false);
    setKeyword('');
  }, []);

  const handleSelect = React.useCallback(
    (nextUuid: string) => {
      if (switchingRef.current) {
        return;
      }
      if (!nextUuid || nextUuid === parkUuid) {
        closePanel();
        return;
      }
      switchingRef.current = true;
      setSwitchingUuid(nextUuid);
      try {
        setParkUuid(nextUuid);
        closePanel();
      } catch {
        message.error('园区切换失败，请稍后重试');
      } finally {
        switchingRef.current = false;
        setSwitchingUuid(undefined);
      }
    },
    [closePanel, parkUuid, setParkUuid],
  );

  const handleApply = React.useCallback(() => {
    if (switching) {
      return;
    }
    closePanel();
    history.push(APPLICATIONS_NEW_PATH);
  }, [closePanel, switching]);

  const parkName =
    displayPark?.name || (parks.length > 0 ? '选择园区' : '暂无入驻园区');
  const triggerLabel = displayPark
    ? `切换园区，当前：${parkName}`
    : parks.length > 0
      ? '请选择园区'
      : '暂无入驻园区，可申请入驻';
  const triggerContent = (
    <>
      <span className="app-park-switcher-icon" aria-hidden="true">
        {switching ? <Spin size="small" /> : <EnvironmentOutlined />}
      </span>
      <span className="app-park-switcher-name">{parkName}</span>
      <span className="app-park-switcher-caret" aria-hidden="true">
        <DownOutlined />
      </span>
    </>
  );

  const panel = (
    <div
      className="app-park-switcher-panel"
      style={{
        background: token.colorBgElevated,
      }}
    >
      {parks.length > SEARCH_THRESHOLD ? (
        <div className="app-park-switcher-search">
          <Input
            allowClear
            size="small"
            prefix={<SearchOutlined />}
            placeholder="搜索园区名称"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      ) : null}
      {displayPark ? (
        <>
          <div className="app-park-switcher-section-title">当前园区</div>
          <ParkItem
            park={displayPark}
            current
            switching={switchingUuid === displayPark.uuid}
            disabled={switching}
            onSelect={handleSelect}
          />
        </>
      ) : null}
      <div className="app-park-switcher-section-title">切换园区</div>
      {filteredParks.map((park) => (
        <ParkItem
          key={park.uuid}
          park={park}
          switching={switchingUuid === park.uuid}
          disabled={switching}
          onSelect={handleSelect}
        />
      ))}
      {filteredParks.length === 0 ? (
        <div className="app-park-switcher-empty">
          {keyword.trim() ? '未找到匹配园区' : '暂无其他可切换园区'}
        </div>
      ) : null}
      <div className="app-park-switcher-footer">
        <button
          type="button"
          className="app-park-switcher-apply"
          disabled={switching}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleApply();
          }}
        >
          <PlusOutlined aria-hidden="true" />
          申请入驻新园区
        </button>
      </div>
    </div>
  );

  return (
    <div className="app-park-switcher">
      <Dropdown
        trigger={['click']}
        open={open}
        onOpenChange={(nextOpen) => {
          if (switching) {
            return;
          }
          setOpen(nextOpen);
          if (!nextOpen) {
            setKeyword('');
          }
        }}
        placement="bottomRight"
        popupRender={() => panel}
        styles={{
          root: {
            padding: 0,
            borderRadius: 10,
            overflow: 'hidden',
          },
        }}
      >
        <button
          type="button"
          className="app-park-switcher-trigger"
          aria-label={triggerLabel}
          aria-expanded={open}
          aria-haspopup="dialog"
          disabled={switching}
          title={parkName}
        >
          {triggerContent}
        </button>
      </Dropdown>
    </div>
  );
};
